import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';

/** One transcribed voice message, as `POST /v1/speech/transcribe` returns it. */
export interface SpeechTranscript {
    text: string;
    /** Languages the provider detected, ISO 639-1; empty when it could not tell. */
    languages: string[];
    durationSeconds: number;
}

/**
 * Where a clip is sent. A signed-in chat bills the caller's own account; a public agent chat bills the
 * agent owner and is addressed by the visitor's session.
 */
export type SpeechTarget = { kind: 'signed-in' } | { kind: 'public'; sessionId: string };

export const SIGNED_IN_SPEECH: SpeechTarget = { kind: 'signed-in' };

/**
 * Client for the transcription endpoints. The body is the recording's raw bytes with its own MIME type
 * as Content-Type, not multipart, so the server can refuse an over-size or unauthorised upload before
 * reading it. Nothing is stored server side; the transcript comes back for the user to edit.
 */
@Injectable({ providedIn: 'root' })
export class SpeechTranscriptionService {
    private readonly _http = inject(HttpClient);
    private readonly _api = environment.apiLink;

    transcribe(audio: Blob, durationMs: number, lang: string, target: SpeechTarget = SIGNED_IN_SPEECH): Observable<SpeechTranscript> {
        let params = new HttpParams().set('durationMs', String(Math.max(1, Math.round(durationMs))));
        if (lang === 'ar' || lang === 'en') {
            params = params.set('lang', lang);
        }
        const url = target.kind === 'public' ? this._api + 'external/chat/transcribe' : this._api + 'speech/transcribe';
        if (target.kind === 'public') {
            params = params.set('sessionId', target.sessionId);
        }
        return this._http
            .post<{ data: SpeechTranscript }>(url, audio, {
                params,
                headers: new HttpHeaders({ 'Content-Type': audio.type }),
            })
            .pipe(map(res => res.data));
    }
}
